import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/providers/auth-provider";
import { autoLoginRoute } from "@/router";

// Driven by the preview-environment helper (.github/scripts/railway-auto-login.js).
export const AutoLogin = () => {
  const [error, setError] = useState("");
  const { onSignIn } = useAuth();
  const navigate = useNavigate();
  const search = autoLoginRoute.useSearch();

  const redirectToLogin = useCallback(
    (email: string) => {
      setError("Setup failed. Redirecting to login page...");
      setTimeout(() => {
        navigate({ to: "/login", search: { email } });
      }, 2000);
    },
    [navigate],
  );

  const attemptAutoLogin = useCallback(
    async (email: string, password: string) => {
      const lowercaseEmail = email.toLowerCase().trim();
      const { error: signInError } = await authClient.signIn.email({ email: lowercaseEmail, password });
      if (signInError) return redirectToLogin(lowercaseEmail);
      await onSignIn();
      navigate({ to: "/" });
    },
    [navigate, onSignIn, redirectToLogin],
  );

  useEffect(() => {
    if (!search.email || !search.password) {
      setError("Missing credentials. Redirecting to login page...");
      setTimeout(() => {
        navigate({ to: "/login" });
      }, 2000);
      return;
    }

    attemptAutoLogin(search.email, search.password);
  }, [search.email, search.password, navigate, attemptAutoLogin]);

  return (
    <div className="flex min-h-screen p-6 gap-6">
      <div className="hidden lg:block lg:w-1/2 relative rounded-lg overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ backgroundImage: "url(/blur-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="absolute top-8 left-8">
            <img src="/white-logo.png" alt="Unbox Logo" className="h-12" />
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 rounded-lg bg-white">
        <div className="absolute top-8 right-8 lg:top-8 lg:right-8">
          <Button
            variant="ghost"
            className="text-gray-600 hover:text-gray-800"
            onClick={() => navigate({ to: "/login" })}
          >
            Log in
          </Button>
        </div>

        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader className="space-y-1 pb-6">
            <h1 className="text-2xl font-bold text-gray-900 text-center">Welcome to Unbox!</h1>
            <p className="text-gray-600 text-center">Setting up your account...</p>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: "#5429B6" }}></div>

                {search.email && (
                  <p className="text-sm text-gray-500">
                    Account: <span className="font-medium">{search.email}</span>
                  </p>
                )}
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
